/*
 * CorpusGrid — clip thumbnails for the idle screen. Fetches GET /corpus once
 * on mount and renders each clip as an animated GIF preview of the real clip
 * (served at /thumbnails/<id>.jpg). On the rare load error, shows a neutral
 * placeholder — NOT a synthetic "highway · day" scene (removed, MLT-512).
 */

import React from 'react';
import { getCorpus } from '../api.js';

function fmtDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = String(Math.round(seconds % 60)).padStart(2, '0');
  return `${m}:${s}`;
}

function fmtIndexedAt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (err) {
    return '';
  }
}

function Thumbnail({ clip }) {
  const [hover, setHover] = React.useState(false);
  const [imgFailed, setImgFailed] = React.useState(false);
  const thumbSrc = `/thumbnails/${encodeURIComponent(clip.id)}.jpg`;
  return (
    <button
      type="button"
      className="thumb focus-ring"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      aria-label={`Clip ${clip.id}`}
    >
      <div className="thumb-frame">
        {imgFailed ? (
          <div className="thumb-blank mono">{clip.id.slice(0, 14)}…</div>
        ) : (
          <img
            className="thumb-img"
            src={thumbSrc}
            alt={`${clip.scene} thumbnail`}
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
          />
        )}
        <div className="thumb-overlay">
          <div className="thumb-tl mono">{clip.id.slice(0, 14)}…</div>
          <div className="thumb-tr mono">{clip.cam}</div>
          <div className="thumb-br mono">{fmtDuration(clip.duration_sec)}</div>
          <div className="thumb-play" style={{ opacity: hover ? 1 : 0.85 }}>
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path d="M7 5 L19 12 L7 19 Z" fill="currentColor" />
            </svg>
          </div>
        </div>
      </div>
      <div className="thumb-meta">
        <div className="thumb-title mono">{clip.scene || '—'}</div>
        <div className="thumb-tags mono">{(clip.tags || []).slice(0, 2).join(' · ')}</div>
      </div>

      <style>{`
        .thumb { all: unset; cursor: default; display: flex; flex-direction: column; gap: 8px; }
        .thumb-frame { position: relative; aspect-ratio: 16/9; overflow: hidden; border: 1px solid var(--rule); background: #000; transition: border-color .15s, transform .25s ease; }
        .thumb:hover .thumb-frame { border-color: var(--rule-2); transform: translateY(-1px); }
        .thumb-img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s ease; }
        .thumb:hover .thumb-img { transform: scale(1.03); }
        .thumb-blank { width: 100%; height: 100%; display: grid; place-items: center; background: #14181d; color: var(--ink-4); font-size: 10px; letter-spacing: 0.04em; }
        .thumb-overlay { position: absolute; inset: 0; pointer-events: none; }
        .thumb-tl, .thumb-tr, .thumb-br {
          position: absolute; font-size: 10px; letter-spacing: 0.04em; color: rgba(255,255,255,0.92);
          padding: 3px 5px; background: rgba(0,0,0,0.42); backdrop-filter: blur(2px);
        }
        .thumb-tl { top: 6px; left: 6px; }
        .thumb-tr { top: 6px; right: 6px; }
        .thumb-br { bottom: 6px; right: 6px; }
        .thumb-play {
          position: absolute; left: 8px; bottom: 8px; width: 22px; height: 22px;
          display: grid; place-items: center; color: rgba(255,255,255,0.95);
          background: rgba(0,0,0,0.42); border-radius: 999px; transition: opacity .15s;
        }
        .thumb-meta { display: flex; justify-content: space-between; gap: 8px; font-size: 11px; color: var(--ink-3); }
        .thumb-title { color: var(--ink-2); letter-spacing: 0.01em; }
        .thumb-tags { color: var(--ink-3); }
      `}</style>
    </button>
  );
}

export function CorpusGrid() {
  const [state, setState] = React.useState({ status: 'loading', corpus: null, error: null });

  React.useEffect(() => {
    let cancelled = false;
    getCorpus()
      .then((corpus) => {
        if (!cancelled) setState({ status: 'ready', corpus, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', corpus: null, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <section className="corpus" aria-label="Corpus">
        <header className="corpus-hd">
          <div className="corpus-hd-l">
            <span className="mono micro">CORPUS</span>
            <span className="serif corpus-count">loading…</span>
          </div>
        </header>
        <CorpusStyles />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="corpus" aria-label="Corpus">
        <header className="corpus-hd">
          <div className="corpus-hd-l">
            <span className="mono micro">CORPUS</span>
            <span className="serif corpus-count">unavailable</span>
          </div>
        </header>
        <p style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>
          {state.error?.message || 'failed to load corpus'}
        </p>
        <CorpusStyles />
      </section>
    );
  }

  const { clips, indexed_at: indexedAt } = state.corpus;

  return (
    <section className="corpus" aria-label="Corpus">
      <header className="corpus-hd">
        <div className="corpus-hd-l">
          <span className="mono micro">CORPUS</span>
          <span className="serif corpus-count num">{clips.length} clips</span>
        </div>
        <div className="corpus-hd-r mono micro">indexed {fmtIndexedAt(indexedAt)}</div>
      </header>
      <div className="corpus-grid">
        {clips.map((c) => (
          <Thumbnail key={c.id} clip={c} />
        ))}
      </div>
      <CorpusStyles />
    </section>
  );
}

function CorpusStyles() {
  return (
    <style>{`
      .corpus { padding: 0 64px 80px; }
      .corpus-hd { display: flex; align-items: baseline; justify-content: space-between; padding: 14px 0; border-bottom: 1px solid var(--rule); margin-bottom: 22px; }
      .corpus-hd-l { display: flex; align-items: baseline; gap: 14px; }
      .corpus-count { font-size: 22px; color: var(--ink); font-weight: 400; }
      .corpus-grid {
        display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 28px 22px;
      }
      @media (max-width: 1100px) { .corpus-grid { grid-template-columns: repeat(3, minmax(0,1fr)); } }
    `}</style>
  );
}
